<script lang="ts" setup>
import { useEventListener } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { computed, onBeforeMount, ref, watch, type Ref } from "vue";

import CollapsibleCard from "../common/CollapsibleCard.vue";

import ActionControls from "./controls/ActionControls.vue";
import BoundsControls from "./controls/BoundsControls.vue";
import ColormapControls from "./controls/ColormapControls.vue";
import DataInput from "./controls/DataInput.vue";
import DimensionControl from "./controls/DimensionControl.vue";
import LayerPanel from "./controls/LayerPanel.vue";
import PopupDialog from "./controls/PopupDialog.vue";
import ProjectionControls from "./controls/ProjectionControls.vue";
import VariableSelector from "./controls/VariableSelector.vue";

// Import control components
import type { T_GRID_TYPES } from "@/lib/data/gridTypeDetector.ts";
import {
  clamp,
  PROJECTION_TYPES,
  type TProjectionType,
} from "@/lib/projection/projectionUtils.ts";
import type {
  TBounds,
  TModelInfo,
  TSnapshotOptions,
} from "@/lib/types/GlobeTypes.ts";
import { useUrlParameterStore } from "@/store/paramStore.ts";
import { useGlobeControlStore } from "@/store/store.ts";
import { MOBILE_BREAKPOINT } from "@/ui/common/viewConstants.ts";

const props = defineProps<{
  modelInfo?: TModelInfo;
  gridType?: T_GRID_TYPES;
  currentSource: string;
  infoPanelOpen: boolean;
}>();

defineEmits<{
  onSnapshot: [options: TSnapshotOptions];
  onRotate: [];
  toggleDisplay: [];
  toggleInfoPanel: [];
}>();

// Bounds management types
const BOUND_MODES = {
  DATA: "data",
  USER: "user",
} as const;

type TBoundModes = (typeof BOUND_MODES)[keyof typeof BOUND_MODES];

const store = useGlobeControlStore();
const {
  colormap,
  invertColormap,
  posterizeLevels,
  varnameSelector,
  landSeaMaskChoice,
  landSeaMaskUseTexture,
  varinfo,
  userBoundsLow,
  userBoundsHigh,
  projectionCenter,
  loading,
} = storeToRefs(store);

// Bounds logic state
const pickedBoundsMode = ref<TBoundModes>(BOUND_MODES.DATA);
let scalarBounds:
  | { low: number | undefined; high: number | undefined; mode: TBoundModes }
  | undefined;

// Colormap logic state
const userHasSelectedColormap = ref<boolean>(false);

const urlParameterStore = useUrlParameterStore();
const {
  paramColormap,
  paramInvertColormap,
  paramPosterizeLevels,
  paramHideLowerBound,
  paramHideUpperBound,
  paramMaskMode,
  paramMaskingUseTexture,
  paramProjection,
  paramLat,
  paramLon,
  paramBoundLow,
  paramBoundHigh,
} = storeToRefs(urlParameterStore);

const menuCollapsed: Ref<boolean> = ref(false);
const mobileMenuCollapsed: Ref<boolean> = ref(true);
const isMobileView: Ref<boolean> = ref(false);

const dataBounds = computed(() => {
  return varinfo.value?.bounds ?? {};
});

const currentBounds = computed(() => {
  if (pickedBoundsMode.value === BOUND_MODES.DATA) {
    return dataBounds.value;
  } else if (pickedBoundsMode.value === BOUND_MODES.USER) {
    const isLowEmpty =
      userBoundsLow.value === undefined ||
      (userBoundsLow.value as unknown as string) === "";
    const isHighEmpty =
      userBoundsHigh.value === undefined ||
      (userBoundsHigh.value as unknown as string) === "";
    const lo = (
      isLowEmpty ? dataBounds.value.low : userBoundsLow.value
    ) as number;
    const hi = (
      isHighEmpty ? dataBounds.value.high : userBoundsHigh.value
    ) as number;
    // Always deliver a normalised (non-inverted) range downstream so that
    // nothing breaks when the user types high < low.  The BoundsControls
    // component shows a visual indicator when the values are swapped.
    return {
      low: lo <= hi ? lo : hi,
      high: lo <= hi ? hi : lo,
    };
  }
  return undefined;
});

const setDefaultBounds = () => {
  const defaultConfig = props.modelInfo?.vars[varnameSelector.value];
  if (defaultConfig?.default_range) {
    userBoundsLow.value = defaultConfig.default_range.low;
    userBoundsHigh.value = defaultConfig.default_range.high;
    return;
  }
};

const setDefaultColormap = () => {
  const defaultColormap =
    props.modelInfo?.vars[varnameSelector.value]?.default_colormap;
  if (!userHasSelectedColormap.value && defaultColormap !== undefined) {
    invertColormap.value = defaultColormap.inverted || false;
    colormap.value = defaultColormap.name;
  }
};

const isHidden = computed(() => {
  return (
    (isMobileView.value && mobileMenuCollapsed.value) || menuCollapsed.value
  );
});

function updateMagnitudeBounds(displayed: boolean) {
  if (displayed) {
    scalarBounds = {
      low: userBoundsLow.value,
      high: userBoundsHigh.value,
      mode: pickedBoundsMode.value,
    };
    store.resetUserBounds();
    pickedBoundsMode.value = BOUND_MODES.DATA;
  } else if (scalarBounds) {
    userBoundsLow.value = scalarBounds.low;
    userBoundsHigh.value = scalarBounds.high;
    pickedBoundsMode.value = scalarBounds.mode;
    scalarBounds = undefined;
  }
}

watch(
  [
    () => varnameSelector.value,
    () =>
      store.streamlineMagnitudeDisplayed && store.streamlineMagnitudeDerivable,
  ],
  ([variable, magnitudeDisplayed], [previousVariable]) => {
    if (variable === previousVariable) {
      updateMagnitudeBounds(magnitudeDisplayed);
      return;
    }
    scalarBounds = undefined;
    // On the very first variable load, URL-provided bounds (already written to
    // the store by HashGlobeView) must not be overwritten by the variable's
    // default_range config.  On subsequent variable changes we always want to
    // reset to the new variable's defaults.
    const preserveUrlBounds =
      store.isNewDataset() &&
      userBoundsLow.value !== undefined &&
      userBoundsHigh.value !== undefined;

    if (!preserveUrlBounds) {
      store.resetUserBounds();
      setDefaultBounds();
    }
    if (
      userBoundsHigh.value === undefined ||
      userBoundsLow.value === undefined
    ) {
      pickedBoundsMode.value = BOUND_MODES.DATA;
    } else {
      pickedBoundsMode.value = BOUND_MODES.USER;
    }
    setDefaultColormap();
    if (magnitudeDisplayed) {
      updateMagnitudeBounds(true);
    }
  }
);

watch(currentBounds, (bounds) => {
  if (bounds) {
    store.updateBounds(bounds as TBounds);
  }
});

function onPickedBoundsModeChange(newMode: TBoundModes) {
  if (newMode === BOUND_MODES.USER) {
    const lowEmpty =
      userBoundsLow.value === undefined ||
      (userBoundsLow.value as unknown as string) === "";
    const highEmpty =
      userBoundsHigh.value === undefined ||
      (userBoundsHigh.value as unknown as string) === "";
    if (lowEmpty) {
      userBoundsLow.value = dataBounds.value.low as number;
    }
    if (highEmpty) {
      userBoundsHigh.value = dataBounds.value.high as number;
    }
  }
  pickedBoundsMode.value = newMode;
}

function toggleMenu() {
  menuCollapsed.value = !menuCollapsed.value;
  store.setControlPanelVisible(!menuCollapsed.value);
}

function toggleMobileMenu() {
  mobileMenuCollapsed.value = !mobileMenuCollapsed.value;
  store.setControlPanelVisible(!mobileMenuCollapsed.value);
}

onBeforeMount(() => {
  isMobileView.value = window.innerWidth < MOBILE_BREAKPOINT;
  initPanelVisibility();
  useEventListener(window, "resize", () => {
    isMobileView.value = window.innerWidth < MOBILE_BREAKPOINT;
  });
});

function initPanelVisibility() {
  const initiallyVisible = isMobileView.value
    ? !mobileMenuCollapsed.value
    : !menuCollapsed.value;
  store.setControlPanelVisible(initiallyVisible);
}

function initDatasetControls() {
  if (!props.modelInfo) {
    return;
  }
  setDefaultBounds();
  store.updateBounds(currentBounds.value as TBounds);
  initFromParams();
}

// eslint-disable-next-line max-lines-per-function
function initFromParams() {
  if (paramColormap.value) {
    colormap.value = paramColormap.value;
    userHasSelectedColormap.value = true;
  }
  if (paramInvertColormap.value) {
    // explicitely check for string values "true" and "false"
    if (paramInvertColormap.value === "false") {
      invertColormap.value = false;
    } else if (paramInvertColormap.value === "true") {
      invertColormap.value = true;
    }
  }
  if (paramPosterizeLevels.value) {
    const levels = Number(paramPosterizeLevels.value);
    if (!isNaN(levels) && levels >= 0 && levels <= 32) {
      posterizeLevels.value = levels;
    }
  }
  if (paramHideLowerBound.value === "true") {
    store.hideLowerBound = true;
  }
  if (paramHideUpperBound.value === "true") {
    store.hideUpperBound = true;
  }
  if (paramMaskingUseTexture.value) {
    if (paramMaskingUseTexture.value === "false") {
      landSeaMaskUseTexture.value = false;
    } else if (paramMaskingUseTexture.value === "true") {
      landSeaMaskUseTexture.value = true;
    }
  }
  if (paramMaskMode.value) {
    landSeaMaskChoice.value =
      paramMaskMode.value as typeof landSeaMaskChoice.value;
  }
  if (paramProjection.value) {
    const projection = paramProjection.value as TProjectionType;
    if (Object.values(PROJECTION_TYPES).includes(projection)) {
      store.projectionMode = projection;
    }
  }
  if (paramLat.value || paramLon.value) {
    const lat = parseFloat(paramLat.value ?? "0");
    const lon = parseFloat(paramLon.value ?? "0");
    projectionCenter.value = {
      lat: clamp(lat, -90, 90),
      lon: clamp(lon, -180, 180),
    };
  }
  if (paramBoundHigh.value && paramBoundLow.value) {
    const low = parseFloat(paramBoundLow.value);
    const high = parseFloat(paramBoundHigh.value);
    userBoundsLow.value = low;
    userBoundsHigh.value = high;
    pickedBoundsMode.value = BOUND_MODES.USER;
  }
}

defineExpose({
  initForDataset: initDatasetControls,
});
</script>

<template>
  <div class="header-container">
    <div class="header-content">
      <div v-if="modelInfo" class="title-bar mobile-title">
        <button
          type="button"
          class="button is-primary is-hidden-tablet p-3 mr-3"
          @click="toggleMobileMenu"
        >
          <i class="fa-solid fa-bars"></i>
        </button>
        <span class="ellipsis" :title="modelInfo.title">
          {{ modelInfo.title }}
        </span>
        <button
          type="button"
          class="borderless-btn dataset-info-trigger ml-1"
          :class="{ 'has-text-info': infoPanelOpen }"
          :title="
            infoPanelOpen
              ? 'Close Dataset Info panel'
              : 'Open Dataset Info panel'
          "
          aria-label="Dataset Info"
          :aria-expanded="infoPanelOpen"
          @click="() => $emit('toggleInfoPanel')"
        >
          <span class="icon">
            <i class="fa-solid fa-circle-info" aria-hidden="true"></i>
          </span>
        </button>
      </div>
      <div v-else-if="loading" class="title-bar">
        <progress class="progress is-info" max="100"></progress>
      </div>
      <div v-else class="title-bar">No data available</div>
      <DataInput :current-source="currentSource" />
      <button
        type="button"
        class="is-hidden-mobile panel-toggle"
        @click="toggleMenu"
      >
        <i
          class="fa-solid"
          :class="{
            'fa-angle-right': menuCollapsed,
            'fa-angle-left': !menuCollapsed,
          }"
        ></i>
      </button>
    </div>
  </div>

  <Transition name="slide">
    <nav
      v-show="!isHidden && (modelInfo || loading)"
      id="main_controls"
      class="gl_controls"
    >
      <div class="full-panel">
        <CollapsibleCard title="Variable">
          <VariableSelector
            v-if="modelInfo"
            v-model="varnameSelector"
            :model-info="modelInfo"
          />
          <DimensionControl />
        </CollapsibleCard>

        <CollapsibleCard title="Appearance">
          <div class="section-title">Colormap</div>
          <BoundsControls
            :picked-bounds-mode="pickedBoundsMode"
            :data-bounds="dataBounds"
            :bound-modes="BOUND_MODES"
            @update:picked-bounds-mode="
              onPickedBoundsModeChange($event as TBoundModes)
            "
          />
          <ColormapControls
            v-if="modelInfo"
            :model-info="modelInfo"
            :data-bounds="dataBounds"
            @colormap-user-selected="userHasSelectedColormap = true"
            @force-user-bounds="pickedBoundsMode = BOUND_MODES.USER"
          />
          <div class="section-title mt-2">Projections</div>
          <ProjectionControls />
          <div class="section-title mt-2 is-flex is-align-items-center">
            Layers and Masks
            <div class="ml-2">
              <PopupDialog dialog-class="layer-help-dialog">
                <template #trigger="{ toggle, open }">
                  <button
                    class="button is-ghost p-0"
                    type="button"
                    title="Layer help"
                    aria-label="Layer help"
                    :aria-expanded="open"
                    @click.stop="toggle"
                  >
                    <span class="icon is-small">
                      <i
                        class="fa-solid fa-circle-question"
                        aria-hidden="true"
                      ></i>
                    </span>
                  </button>
                </template>

                <template #default>
                  <p class="dialog-section-label">Layers</p>
                  <p class="is-size-7 mb-2">
                    Drag layers up or down to change their drawing order.
                  </p>
                  <p class="has-text-danger is-light is-size-7 mb-0">
                    <strong class="has-text-danger">Warning:</strong> Custom
                    layers are stored only in this browser and cannot be shared
                    via URL.
                  </p>
                </template>
              </PopupDialog>
            </div>
          </div>
          <LayerPanel :model-info="modelInfo" :grid-type="gridType" />
        </CollapsibleCard>
        <CollapsibleCard title="Actions">
          <ActionControls
            @on-snapshot="(opts) => $emit('onSnapshot', opts)"
            @on-rotate="() => $emit('onRotate')"
            @toggle-display="() => $emit('toggleDisplay')"
          />
        </CollapsibleCard>
      </div>
    </nav>
  </Transition>
</template>

<style lang="scss">
@use "bulma/sass/utilities" as bulmaUt;

.header-content .data-input-trigger {
  order: 0;
}

.title-bar {
  order: 1;
}

.panel-toggle {
  order: 2;
}

.dataset-info-trigger {
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  color: inherit;
  background: transparent;
  font-size: 1.6rem;
  opacity: 0.8;

  &:hover {
    color: inherit;
    background: rgb(255 255 255 / 12%);
    opacity: 1;
  }

  &.has-text-info {
    color: var(--bulma-info) !important;
    opacity: 1;
  }
}

.header-container {
  flex-wrap: nowrap;
  overflow: visible;
  border-radius: 0;
  font-size: 1.1rem;
  position: fixed;

  width: 24rem;
  z-index: 10;
  height: 56px;
  display: flex;
  align-items: center;
  background-color: var(--bulma-body-color);
  color: white;
  font-weight: bold;
  padding: 0 16px;
  @media only screen and (max-width: bulmaUt.$tablet) {
    width: 100%;
  }
}

.header-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: space-between;
  width: 100%;
}

.header-content > button {
  flex-shrink: 0;
}

.mobile-title {
  display: flex;
  align-items: center;
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
}

.mobile-title > button {
  flex-shrink: 0;
}

.ellipsis {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1 1 auto;
}

@media only screen and (max-width: bulmaUt.$tablet) {
  .header-content .mobile-title {
    flex: 1;
    min-width: 0;
    overflow: auto;
  }

  .header-content .data-input-trigger {
    order: 2;
    margin-left: auto;
  }
}

@media (prefers-color-scheme: dark) {
  .header-container {
    background: var(--bulma-scheme-main);
  }
  .gl_controls {
    background-color: var(--bulma-scheme-main) !important;
    .box {
      color: rgb(235, 236, 240) !important;
    }
  }
}

.gl_controls {
  scrollbar-width: thin;
  margin-top: 56px;
  width: 24rem;
  min-width: 0;
  height: calc(100vh - 56px);
  overflow-y: auto;
  overscroll-behavior-x: none;
  flex-shrink: 0;
  z-index: 10;
  background-color: #ddd;
  color: black;

  .panel-block {
    padding: 0.75em 0.8em;
  }

  .column {
    padding: 0.5em;
  }

  input {
    margin-right: 3px;
  }

  @media only screen and (max-width: bulmaUt.$tablet) {
    width: 100%;
    position: fixed;
    height: 95%;
    border-radius: 0 !important;
    padding-bottom: calc(8rem + env(safe-area-inset-bottom, 0px));

    &.mobile-visible {
      max-height: 100vh;
    }
  }

  &.slide-enter-active,
  &.slide-leave-active {
    transition: width 0.3s ease-out;
  }

  &.slide-enter-from,
  &.slide-leave-to {
    width: 0;
  }

  @media only screen and (max-width: bulmaUt.$tablet) {
    &.slide-enter-active,
    &.slide-leave-active {
      transition: height 0.3s ease-in;
    }

    &.slide-enter-from,
    &.slide-leave-to {
      width: 100%;
      height: 0;
    }
  }
}
</style>
